import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { authClient, signIn } from "@/lib/auth-client";
import {
  DEV_ADMIN_PASSWORD,
  DEV_MANAGER_PASSWORD,
  DEV_USER_PASSWORD,
  DEV_VIEWER_PASSWORD,
} from "@/lib/dev-accounts";
import { authMethodKeys, devKeys } from "@/lib/query";

const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

type LoginValues = z.infer<typeof loginSchema>;

const isDev = process.env.NODE_ENV !== "production";

const seedAccountSchema = z.object({ email: z.string().email(), name: z.string() });
const seedAccountsSchema = z.object({
  admin: seedAccountSchema,
  manager: seedAccountSchema,
  viewer: seedAccountSchema,
  user: seedAccountSchema,
});

type DevAccount = { role: string; personName: string; email: string; password: string };

/**
 * Whether this environment offers Microsoft sign-in.
 *
 * Read from the server rather than from an environment variable in the bundle:
 * the app registration is per-environment, and the sign-in page renders before
 * anyone has a session. A failed read means "no" — offering a button that
 * cannot work is worse than not offering one.
 */
function useMicrosoftSignIn(): boolean {
  const { data } = useQuery({
    queryKey: authMethodKeys.all,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async (): Promise<boolean> => {
      const res = await fetch("/api/auth-methods");
      if (!res.ok) return false;
      const parsed = z.object({ microsoft: z.boolean() }).safeParse(await res.json());
      return parsed.success && parsed.data.microsoft;
    },
  });
  return data ?? false;
}

/** Microsoft's brand mark, from their official four-square logo. */
function MicrosoftMark() {
  return (
    <svg viewBox="0 0 21 21" className="size-4" aria-hidden="true">
      <title>Microsoft</title>
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

// Emails/names come from a dev-only server endpoint so they always match the
// seeded rows, regardless of which host the studio opened the app at. The
// `name` is the matching `person.name` from the test-data seed so each demo
// account links to a real person profile in the system.
function useDevAccounts() {
  return useQuery({
    queryKey: devKeys.seedAccounts,
    enabled: isDev,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async (): Promise<DevAccount[]> => {
      const res = await fetch("/api/dev/seed-accounts");
      if (!res.ok) return [];
      const parsed = seedAccountsSchema.parse(await res.json());
      return [
        {
          role: "Admin",
          personName: parsed.admin.name,
          email: parsed.admin.email,
          password: DEV_ADMIN_PASSWORD,
        },
        {
          role: "Manager",
          personName: parsed.manager.name,
          email: parsed.manager.email,
          password: DEV_MANAGER_PASSWORD,
        },
        {
          role: "Admin (view only)",
          personName: parsed.viewer.name,
          email: parsed.viewer.email,
          password: DEV_VIEWER_PASSWORD,
        },
        {
          role: "User",
          personName: parsed.user.name,
          email: parsed.user.email,
          password: DEV_USER_PASSWORD,
        },
      ];
    },
  });
}

export function LoginForm() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });
  const devAccounts = useDevAccounts().data ?? [];
  const microsoftEnabled = useMicrosoftSignIn();
  const [redirecting, setRedirecting] = useState(false);

  async function signInWithMicrosoft() {
    setRedirecting(true);
    // Leaves the app for Microsoft and comes back to the callback address, so
    // there is no success path to handle here — only a failure to start, which
    // means the provider is misconfigured on the server.
    const { error } = await authClient.signIn.oauth2({
      providerId: "microsoft-entra-id",
      callbackURL: "/dashboard/my",
    });
    if (error) {
      setRedirecting(false);
      toast.error(error.message ?? "Could not start Microsoft sign-in");
    }
  }

  function fillCredentials(email: string, password: string) {
    form.setValue("email", email, { shouldValidate: true });
    form.setValue("password", password, { shouldValidate: true });
  }

  async function onSubmit(values: LoginValues) {
    const { error } = await signIn.email({
      email: values.email,
      password: values.password,
    });

    if (error) {
      toast.error(error.message ?? "Failed to sign in");
      return;
    }

    // Drop every cached query from the previous session — per-user data
    // (my-person, direct reports, announcements) must not leak across logins.
    queryClient.clear();

    // Everyone lands on My Dashboard — the same page the sidebar's
    // Dashboard link points to.
    router.navigate({ to: "/dashboard/my" });
  }

  return (
    <Card className="border-border/50 shadow-sm">
      <CardHeader className="space-y-1 text-center pb-2">
        <h1 className="font-display text-3xl font-bold">Sign In</h1>
        <p className="text-muted-foreground text-sm">Enter your credentials to continue</p>
      </CardHeader>
      <CardContent>
        {microsoftEnabled && (
          <div className="mb-4 space-y-4">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={redirecting}
              onClick={() => void signInWithMicrosoft()}
            >
              <MicrosoftMark />
              {redirecting ? "Redirecting…" : "Sign in with Microsoft"}
            </Button>
            <div className="flex items-center gap-3">
              <span className="h-px flex-1 bg-border" />
              <span className="text-muted-foreground text-xs">or</span>
              <span className="h-px flex-1 bg-border" />
            </div>
          </div>
        )}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="you@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="••••••••" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? "Signing in…" : "Sign In"}
            </Button>
          </form>
        </Form>
        {isDev && devAccounts.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className="text-center text-xs text-muted-foreground">Sign In As:</p>
            <div className="grid grid-cols-3 gap-2">
              {devAccounts.map((account) => (
                <Button
                  key={account.role}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex h-auto flex-col gap-0.5 py-2"
                  onClick={() => fillCredentials(account.email, account.password)}
                >
                  <span className="text-sm font-medium">{account.personName}</span>
                  <span className="text-xs text-muted-foreground">{account.role}</span>
                </Button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
